/**
 * masterGanttCsvExport.ts
 * Phase 4, Item 5 — Pure CSV export helper for the Master Gantt.
 * Follow-up 1.5 — `filterCsvBySelection` for bulk-export of selected bars.
 *
 * `responseToCsv` converts a MasterGanttResponse into a RFC-4180 CSV string.
 * It is a pure function with no browser/DOM dependencies, so it is fully
 * unit-testable without a DOM environment.
 *
 * `filterCsvBySelection` is a variant that accepts a Set<string> of booking/
 * assignment keys and emits only the matching rows. Also pure; no DOM.
 *
 * Column schema (one row per booking + one per assignment):
 *   ProjectName, WorkOrderNumber, Type, Resource, TaskName,
 *   StartDate, EndDate, Status, Source
 *
 * Type = "Machine" for MachineBookings, "Employee" for EmployeeAssignments.
 * Resource = machineCode for machine rows, displayName for employee rows.
 *
 * `triggerCsvDownload` wraps the string in a Blob and clicks a hidden <a> tag
 * to download it. It is separated so tests can cover `responseToCsv` without
 * needing jsdom.
 */

import type {
  MasterGanttResponse,
  MasterGanttProject,
  MasterGanttWorkOrder,
  MasterGanttMachineBooking,
  MasterGanttEmployeeAssignment,
} from '../../../types/schedule.js';

// ---------------------------------------------------------------------------
// CSV helpers
// ---------------------------------------------------------------------------

/** Escape a single CSV cell value per RFC-4180. */
function escapeCell(value: string | null | undefined): string {
  const s = value == null ? '' : String(value);
  // Wrap in double-quotes if the field contains commas, newlines, or double-quotes.
  if (s.includes(',') || s.includes('\n') || s.includes('"')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Join an array of cell values into a CSV row string. */
function toCsvRow(cells: (string | null | undefined)[]): string {
  return cells.map(escapeCell).join(',');
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const CSV_HEADER = toCsvRow([
  'ProjectName',
  'WorkOrderNumber',
  'Type',
  'Resource',
  'TaskName',
  'StartDate',
  'EndDate',
  'Status',
  'Source',
]);

// ---------------------------------------------------------------------------
// Row builders
// ---------------------------------------------------------------------------

function buildBookingRow(
  project: MasterGanttProject,
  wo: MasterGanttWorkOrder,
  booking: MasterGanttMachineBooking,
): string {
  return toCsvRow([
    project.projectName,
    wo.workOrderNumber,
    'Machine',
    booking.machineCode,
    booking.taskName,
    booking.startDate,
    booking.endDate,
    booking.status,
    booking.source,
  ]);
}

function buildAssignmentRow(
  project: MasterGanttProject,
  wo: MasterGanttWorkOrder,
  assignment: MasterGanttEmployeeAssignment,
): string {
  // Use startDate if available, otherwise fall back to weekStart.
  const start = assignment.startDate ?? assignment.weekStart;
  // End date may be null for weekly-only records; use weekStart as fallback.
  const end = assignment.endDate ?? assignment.weekStart;

  return toCsvRow([
    project.projectName,
    wo.workOrderNumber,
    'Employee',
    assignment.displayName,
    assignment.taskName,
    start,
    end,
    assignment.status,
    assignment.source,
  ]);
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Convert a MasterGanttResponse to a CSV string.
 *
 * Order: bookings before assignments within each work order;
 * work orders in the order returned by the API;
 * projects in the order returned by the API.
 *
 * Pure function — no browser APIs used.
 */
export function responseToCsv(response: MasterGanttResponse): string {
  const rows: string[] = [CSV_HEADER];

  for (const project of response.projects) {
    for (const wo of project.workOrders) {
      for (const booking of wo.machineBookings) {
        rows.push(buildBookingRow(project, wo, booking));
      }
      for (const assignment of wo.employeeAssignments) {
        rows.push(buildAssignmentRow(project, wo, assignment));
      }
    }
  }

  return rows.join('\r\n');
}

/**
 * Follow-up 1.5 — Bulk Export (selected bars only).
 *
 * Converts a MasterGanttResponse to a CSV string filtered to only the bars
 * whose `bookingKey` or `assignmentKey` is present in `selectedKeys`.
 *
 * Pure function — no browser APIs used. Safe to unit-test without jsdom.
 *
 * @param response     The full master-gantt API response.
 * @param selectedKeys Set of booking/assignment key strings from the multi-select.
 * @returns            RFC-4180 CSV string (header + matching rows only).
 */
export function filterCsvBySelection(
  response: MasterGanttResponse,
  selectedKeys: Set<string>,
): string {
  if (selectedKeys.size === 0) return CSV_HEADER;

  const rows: string[] = [CSV_HEADER];

  for (const project of response.projects) {
    for (const wo of project.workOrders) {
      for (const booking of wo.machineBookings) {
        if (selectedKeys.has(booking.bookingKey)) {
          rows.push(buildBookingRow(project, wo, booking));
        }
      }
      for (const assignment of wo.employeeAssignments) {
        if (selectedKeys.has(assignment.assignmentKey)) {
          rows.push(buildAssignmentRow(project, wo, assignment));
        }
      }
    }
  }

  return rows.join('\r\n');
}

// ---------------------------------------------------------------------------
// Browser download trigger (separate so tests can stub it out)
// ---------------------------------------------------------------------------

/**
 * Trigger a browser download of `csvString` as `filename`.
 * Uses a Blob + temporary <a> element (standard "download via link" pattern).
 * Has no return value and cannot be unit-tested without jsdom.
 */
export function triggerCsvDownload(csvString: string, filename: string): void {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Clean up after a short delay to allow the download to start.
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}
